import { createTheme, alpha, type Theme } from "@mui/material/styles";
import type { Shadows } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { SECONDARY, SEMANTICS, THEMES } from "./palettes";
import type { ColorMode, Density, ShellPalette, ThemeId } from "./types";
import type { ModuleKey } from "./modules";

/**
 * HealInventory enterprise UI system. The thesis is restraint: a neutral canvas carries ~90% of the
 * UI, a single confident accent, and success/warning/error appear only as state (never decoration).
 * Numbers use tabular figures so columns align. Dark mode is a night variant with the same
 * discipline.
 *
 * Tokens live in `palettes.ts` (the registry) and `palettes.generated.ts` (the OKLCH ramps).
 * Screens read them via the theme and should never reach for a raw hex.
 */

export type { ColorMode, Density, NeutralTokens, PaletteColor, SemanticTokens, ShellPalette, ThemeDef, ThemeId } from "./types";
export { THEMES, THEME_ORDER, DEFAULT_THEME_ID, isThemeId, DEPARTMENT_COLORS, SEMANTICS, SECONDARY } from "./palettes";
export type { DepartmentKey } from "./palettes";
export { resolveModule, MODULE_LABELS } from "./modules";
export type { ModuleKey } from "./modules";

/**
 * The sidebar is chrome, not content: it keeps its own deep palette in both modes rather than
 * following `background.paper`. Hanging it off the theme means `Layout` reads whichever shell
 * belongs to the active theme without needing to know which theme that is.
 */
declare module "@mui/material/styles" {
  interface Theme {
    shell: ShellPalette;
  }
  interface ThemeOptions {
    shell?: ShellPalette;
  }
}

/** Soft, layered slate-tinted shadow ramp — refined depth instead of MUI's heavier defaults. */
function softShadows(): Shadows {
  const make = (e: number) =>
    `0px ${e}px ${e * 2}px ${-Math.ceil(e / 2)}px rgba(15,24,38,0.10), ` +
    `0px ${Math.ceil(e / 2)}px ${e}px ${-Math.ceil(e / 2)}px rgba(15,24,38,0.05)`;
  const arr: string[] = ["none"];
  for (let e = 1; e <= 24; e++) arr.push(make(e));
  return arr as unknown as Shadows;
}

export function buildTheme(
  themeId: ThemeId = "slate",
  mode: ColorMode = "light",
  density: Density = "comfortable",
  /** Only consulted for themes that define `moduleAccents` — i.e. "departmental". */
  moduleKey: ModuleKey | null = null,
): Theme {
  const def = THEMES[themeId] ?? THEMES.slate;
  const t = def.neutral[mode];
  const semantic = SEMANTICS[mode];
  const dense = density === "compact";

  // The one thing a module changes is the accent. Everything below reads `palette.primary`, so
  // buttons, links, focus rings, the active nav item, KPI tiles and grid hover all follow for free.
  const accent = (moduleKey && def.moduleAccents?.[moduleKey]?.[mode]) ?? def.accent[mode];
  const shell =
    accent === def.accent[mode]
      ? def.shell[mode]
      : { ...def.shell[mode], activeBg: alpha(accent.main, mode === "dark" ? 0.18 : 0.2) };

  return createTheme({
    shell,
    palette: {
      mode,
      primary: accent,
      secondary: SECONDARY[mode],
      success: semantic.success,
      warning: semantic.warning,
      info: semantic.info,
      error: semantic.error,
      background: { default: t.background, paper: t.paper },
      text: { primary: t.textPrimary, secondary: t.textSecondary },
      divider: t.divider,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeightMedium: 500,
      h4: { fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em" }, // 28px
      h5: { fontSize: "1.375rem", fontWeight: 680, letterSpacing: "-0.02em" }, // 22px — page title
      h6: { fontSize: "1.25rem", fontWeight: 650, letterSpacing: "-0.01em" },  // 20px — section heading
      subtitle1: { fontSize: "1.0625rem", fontWeight: 600 },                   // 17px
      subtitle2: { fontSize: "0.875rem", fontWeight: 600 },                    // 14px — card labels
      body1: { fontSize: "0.9375rem" }, // 15px
      body2: { fontSize: "0.8125rem" }, // 13px — table data / secondary
      caption: { fontSize: "0.75rem" }, // 12px — helper text
      button: { textTransform: "none", fontWeight: 600 },
    },
    shadows: softShadows(),
    components: {
      // Tabular figures everywhere digits appear — the single biggest "enterprise data" signal.
      MuiCssBaseline: {
        styleOverrides: {
          body: { fontVariantNumeric: "tabular-nums" },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8 },
          sizeSmall: { paddingTop: 5, paddingBottom: 5 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}` }),
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontSize: "0.875rem" } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: t.dividerStrong },
        },
      },
      // Plain MUI tables: quiet hairlines, uppercase muted headers on a faint fill.
      MuiTableHead: {
        styleOverrides: {
          root: { "& .MuiTableCell-head": { backgroundColor: t.surface2 } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.divider,
            ...(dense && { paddingTop: 6, paddingBottom: 6 }),
          }),
          head: {
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: t.textSecondary,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&:hover": { backgroundColor: alpha(theme.palette.primary.main, mode === "dark" ? 0.08 : 0.035) },
          }),
        },
      },
      // Soft tinted status pills instead of loud solid Chips.
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const c = ownerState.color;
            if (ownerState.variant !== "outlined" && c && c !== "default") {
              const pal = theme.palette[c] as { main: string; light: string; dark: string } | undefined;
              if (pal?.main) {
                return {
                  backgroundColor: alpha(pal.main, theme.palette.mode === "dark" ? 0.22 : 0.14),
                  color: theme.palette.mode === "dark" ? pal.light : pal.dark,
                  fontWeight: 600,
                  borderColor: "transparent",
                };
              }
            }
            return {};
          },
        },
      },
      // App-wide DataGrid styling: hairline frame, quiet uppercase headers, calm hover, no focus rings.
      MuiDataGrid: {
        defaultProps: { density: dense ? "compact" : "standard" },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            fontSize: "0.8125rem",
            backgroundColor: theme.palette.background.paper,
            "--DataGrid-rowBorderColor": theme.palette.divider,
            "--DataGrid-containerBackground": t.surface2,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: theme.palette.text.secondary,
            },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .MuiDataGrid-cell": { borderColor: theme.palette.divider },
            // Row-action icons, centred. The grid centres cell content with
            // `line-height: <row height>` rather than flexbox, and only switches a cell to
            // `display:flex` when the column opts in — which the built-in actions/boolean/checkbox
            // column types do and a plain column with a renderCell does not. Every listing screen
            // builds its edit/delete column as a plain column named "actions", so its IconButton
            // sat on that tall line box's baseline and rode up towards the top of the row.
            // Flexing the cell is the fix MUI intends, and it is safe to do globally *here*
            // because this column holds no text: nothing loses the cell's text-overflow ellipsis,
            // which is why the same rule is not applied to cells at large.
            '& .MuiDataGrid-cell[data-field="actions"]': {
              display: "flex",
              alignItems: "center",
              lineHeight: "inherit",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha(theme.palette.primary.main, mode === "dark" ? 0.08 : 0.035),
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-footerContainer": { borderTopColor: theme.palette.divider },
          }),
        },
      },
    },
  });
}
